import { useCallback } from "react";
import cx from "classnames";
import { Em, Flex, ScrollArea, Text, Badge } from "@radix-ui/themes";
import { TextWithTooltip } from "@/components/shared/tooltip";
import { ExploreAggInteractionRow } from "@/v2/entities/explore/Explore.model";
import { ExploreGroupByColumnType } from "@/v2/entities/explore/columns/ExploreColum.model";
import styles from "./InteractionsList.module.scss";
import { AvgQaScoreFormatter } from "@/v2/components/formatters/AvgQaScoreFormatter";

interface InteractionItemProps {
  groupBy: ExploreGroupByColumnType;
  interaction: ExploreAggInteractionRow;
  selectedInteractionId?: string;
  onSelect: (interaction: ExploreAggInteractionRow) => void;
}

function AggInteractionItem({
  groupBy,
  interaction,
  selectedInteractionId,
  onSelect,
}: InteractionItemProps) {
  const handleSelect = useCallback(() => {
    if (interaction.id == null) {
      return;
    }

    onSelect(interaction);
  }, [interaction.id, onSelect]);

  return (
    <Flex
      key={interaction.id}
      p="3"
      direction="column"
      gap="1"
      onClick={handleSelect}
      className={cx(styles.interaction, {
        [styles.active]: selectedInteractionId === interaction.id,
      })}
    >
      <Flex justify="between" align="center" gap="2">
        <TextWithTooltip
          size="2"
          color="gray"
          weight="medium"
          highContrast
          truncate
        >
          {groupBy === "team" && (interaction.team ?? <Em>Unknown Team</Em>)}
          {groupBy === "agentAssigned" &&
            (interaction.agentAssigned ?? <Em>Unknown Agent</Em>)}
        </TextWithTooltip>

        <Flex align="center" gap="1">
          <Badge size="1" color="gray" variant="soft">
            {interaction.volume.toLocaleString()}
          </Badge>
        </Flex>
      </Flex>

      <Text size="1">
        <AvgQaScoreFormatter value={interaction.avgQaScore} />
      </Text>
    </Flex>
  );
}

interface AggInteractionsListProps {
  groupBy: ExploreGroupByColumnType;
  loading: boolean;
  interactions?: ExploreAggInteractionRow[];
  selectedInteractionId?: string;
  onSelect: (interaction: ExploreAggInteractionRow) => void;
}

export function AggInteractionsList({
  groupBy,
  interactions,
  selectedInteractionId,
  onSelect,
}: AggInteractionsListProps) {
  return (
    <Flex
      width="100%"
      height="100%"
      direction="column"
      overflow="hidden"
      className={styles.interactionsList}
    >
      <ScrollArea
        scrollbars={"vertical"}
        style={{ width: "100%", height: "100%" }}
      >
        <Flex p="3" height="100%" direction="column" gap="1">
          {interactions?.length === 0 && (
            <Flex flexGrow="1" p="4" justify="center">
              <Text size="2" color="gray">
                No Items Found
              </Text>
            </Flex>
          )}

          {interactions?.map((interaction) => (
            <AggInteractionItem
              groupBy={groupBy}
              interaction={interaction}
              selectedInteractionId={selectedInteractionId}
              onSelect={onSelect}
            />
          ))}
        </Flex>
      </ScrollArea>
    </Flex>
  );
}
